import React, { useState, useRef } from "react";
import Logo from "./Logo";
import { mergeVideos, downloadBlob } from "../services/ffmpeg";
import "./UploadScreen.css";

// File size limits using binary unit conversions (1 GB = 1024 MB)
const GB = 1024 * 1024 * 1024;
const MAX_MERGE_SIZE = 0.99 * GB;
const MAX_MERGE_FILES = 5;

const UploadScreen = ({ onUpload, onOpenSplitter, message = "" }) => {
  const [error, setError] = useState("");
  const [isMerging, setIsMerging] = useState(false);
  const [mergeProgress, setMergeProgress] = useState(0);
  const mergeInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError("");
    onUpload(file);
  };

  const handleMergeFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (files.length > MAX_MERGE_FILES) {
      setError(`You can only select up to ${MAX_MERGE_FILES} files at once.`);
      return;
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > MAX_MERGE_SIZE) {
      setError("Total file size exceeds maximum limit of 0.99GB for merging.");
      return;
    }

    const sortedFiles = [...files].sort((a, b) => a.name.localeCompare(b.name));

    if (!window.confirm(`Merge these ${files.length} files?\n\n${sortedFiles.map((f) => f.name).join("\n")}`)) {
      if (mergeInputRef.current) mergeInputRef.current.value = "";
      return;
    }

    setError("");
    setIsMerging(true);
    setMergeProgress(0);
    try {
      const { blob, filename } = await mergeVideos(sortedFiles, { onProgress: setMergeProgress });
      downloadBlob(blob, filename);
    } catch (err) {
      console.error("Error merging files:", err);
      setError(`Error merging files: ${err.message}`);
    } finally {
      setIsMerging(false);
      if (mergeInputRef.current) mergeInputRef.current.value = "";
    }
  };

  return (
    <div className="upload-screen">
      <Logo />

      <p className="upload-description">Create Highlight videos fast and hassle free!</p>
      {message && <div className="info-message">{message}</div>}
      <p></p>
      <label htmlFor="video-upload" className="upload-label">
        <input
          type="file"
          id="video-upload"
          accept="video/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <div className="upload-box">Upload video to begin</div>
      </label>
      <div className="file-restriction-info">
        <p>Videos of any size are processed directly in your browser; nothing is uploaded.</p>
        {onOpenSplitter && (
          <p>
            Need smaller files?{" "}
            <button type="button" className="link-button" onClick={onOpenSplitter}>
              Split a large video into parts
            </button>
          </p>
        )}
      </div>

      <p></p>
      <label htmlFor="merge-files-upload" className={`merge-label ${isMerging ? "disabled" : ""}`}>
        <input
          type="file"
          id="merge-files-upload"
          accept="video/*"
          multiple
          disabled={isMerging}
          onChange={handleMergeFileSelect}
          ref={mergeInputRef}
          style={{ display: "none" }}
        />
        <div className="upload-box">
          {isMerging ? `Merging... ${Math.round(mergeProgress * 100)}%` : "Merge multiple Highlight movies"}
        </div>
      </label>
      <div className="file-restriction-info">
        <p>Select up to 5 video files to merge into one highlight video.</p>
        <p>Files will be merged in alphabetical order by filename.</p>
        <p>Total size limit: 0.99GB</p>
      </div>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default UploadScreen;
